import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { LocationSelectors, LocationsByCompanySelectors } from '@core/locations-data';
import type { Location } from '@core/locations-types';

import type { LocationItem } from './select-locations-dialog.types';

@Injectable()
export class SelectLocationsDialogDataService {
  private store = inject(Store);

  public allLocationsList$(
    assignedLocationIds: number[],
    hideInvalidLocations: boolean,
  ): Observable<LocationItem[]> {
    return this.store.select(LocationSelectors.allEntities).pipe(
      filterBoolean,
      map(locations =>
        this.getLocationItems(locations)
          .filter(
            location =>
              !(location.isArchived || (!location.isValid && hideInvalidLocations)) ||
              assignedLocationIds.includes(location.id),
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
    );
  }

  public locationsListByIds$(
    locationsIds: number[],
    assignedLocationIds: number[],
    hideInvalidLocations: boolean,
    companyId?: number,
  ): Observable<LocationItem[]> {
    const combinedIds = [...new Set([...locationsIds, ...assignedLocationIds])];
    const locationsSource$ = companyId
      ? this.store.select(LocationsByCompanySelectors.byId(companyId)).pipe(
          filterBoolean,
          map(data => data.locations.filter(location => combinedIds.includes(location.id))),
        )
      : this.store.select(LocationSelectors.byIds(combinedIds));

    return locationsSource$.pipe(
      filterBoolean,
      map(locations =>
        this.getLocationItems(locations, locationsIds)
          .filter(
            location =>
              !(location.isArchived || (!location.isValid && hideInvalidLocations)) ||
              assignedLocationIds.includes(location.id),
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
    );
  }

  private getLocationItems(locations: Location[], availableLocationIds?: number[]): LocationItem[] {
    return locations.map(location => {
      const { id, name, fullAddress, countryCode, stateName, city, archivedAt, isValid } = location;
      return {
        id,
        name,
        fullAddress,
        countryCode,
        stateName,
        city,
        isValid,
        isArchived: !!archivedAt,
        isRemoved: availableLocationIds ? !availableLocationIds.includes(id) : false,
      };
    });
  }
}
